/**
 * Page reference collector (page builder Data dock — "where used").
 *
 * A single pure pass over the page AST that answers BOTH directions:
 *   - forward: which components use a given `$queries.<name>` / `$page.<var>`;
 *   - inverse: which queries/vars a given component reads/writes.
 *
 * It powers the usage inspector, the delete-guard (don't drop a referenced
 * binding without warning), and the "unused" lint.
 *
 * ⚠️ STATIC, best-effort. References are found by scanning `{{ }}` templates +
 * known structured fields. It CANNOT see inside a `custom_component` (it gets
 * the whole model) or a JS query (`ctx.db…` / dynamic access). So `hasOpaque`
 * flags when such consumers exist — "0 uses" then means "no STATIC use found",
 * NOT "safe to delete".
 */
import type { PageAst, PageNode, PageQuery } from "./pageTypes";

export type RefDirection = "read" | "write";

export interface Usage {
  /** AST node id (a page node, or a query id when a query is the consumer). */
  nodeId: string;
  /** Friendly label of the consumer (component title/type, or `query: name`). */
  label: string;
  /** Where the reference sits (a prop key, `visible_when`, `deps`, …). */
  field: string;
  direction: RefDirection;
}

export interface PageReferences {
  /** query name → consumers. */
  query: Record<string, Usage[]>;
  /** page-variable name → consumers. */
  var: Record<string, Usage[]>;
  /** per page-node: what it reads/writes (the inverse view). */
  byNode: Record<string, { queriesRead: string[]; varsRead: string[]; varsWritten: string[] }>;
  /** True when an opaque consumer (custom_component / js query) exists — so a
   *  "0 uses" verdict must be qualified, never trusted as "deletable". */
  hasOpaque: boolean;
}

const PAGE_RE = /\$page\.([A-Za-z_$][\w$]*)/g;
const QUERY_RE = /\$queries\.([A-Za-z_$][\w$]*)/g;

/** Extract `$page.x` / `$queries.y` identifiers from a string (word-bounded, so
 *  `$page.search` ≠ `$page.searchText`). Bracket access (`$page[k]`) is dynamic
 *  and intentionally NOT matched (covered by the `hasOpaque` caveat). */
function scan(str: string): { vars: string[]; queries: string[] } {
  const vars: string[] = [];
  const queries: string[] = [];
  let m: RegExpExecArray | null;
  PAGE_RE.lastIndex = 0;
  while ((m = PAGE_RE.exec(str))) vars.push(m[1]);
  QUERY_RE.lastIndex = 0;
  while ((m = QUERY_RE.exec(str))) queries.push(m[1]);
  return { vars, queries };
}

/** A friendly label for a node consumer. */
function nodeLabel(node: PageNode): string {
  const p = node.props ?? {};
  const name = p.title ?? p.label ?? p.name ?? p.heading;
  return typeof name === "string" && name.trim() ? `${node.type} · ${name}` : node.type;
}

export function findReferences(ast: PageAst): PageReferences {
  const refs: PageReferences = { query: {}, var: {}, byNode: {}, hasOpaque: false };

  const addQuery = (name: string, u: Usage) => {
    if (!name) return;
    (refs.query[name] ??= []).push(u);
  };
  const addVar = (name: string, u: Usage) => {
    if (!name) return;
    (refs.var[name] ??= []).push(u);
  };
  const node = (id: string) =>
    (refs.byNode[id] ??= { queriesRead: [], varsRead: [], varsWritten: [] });
  const uniqPush = (arr: string[], v: string) => {
    if (v && !arr.includes(v)) arr.push(v);
  };

  type Ctx = { nodeId: string; label: string; field: string; isNode: boolean };
  const mk = (ctx: Ctx, direction: RefDirection): Usage => ({
    nodeId: ctx.nodeId,
    label: ctx.label,
    field: ctx.field,
    direction,
  });

  /** Deep-walk any value: pull `{{ }}` reads from strings + `set_variable`
   *  writes from action objects. `ctx` attributes each find to a consumer. */
  const walk = (value: unknown, ctx: Ctx) => {
    if (typeof value === "string") {
      const { vars, queries } = scan(value);
      for (const v of vars) {
        addVar(v, mk(ctx, "read"));
        if (ctx.isNode) uniqPush(node(ctx.nodeId).varsRead, v);
      }
      for (const q of queries) {
        addQuery(q, mk(ctx, "read"));
        if (ctx.isNode) uniqPush(node(ctx.nodeId).queriesRead, q);
      }
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((v) => walk(v, ctx));
      return;
    }
    if (value && typeof value === "object") {
      const obj = value as Record<string, unknown>;
      // An action-chain `set_variable` writes a page var by name.
      if (obj.type === "set_variable" && typeof obj.name === "string") {
        addVar(obj.name, mk(ctx, "write"));
        if (ctx.isNode) uniqPush(node(ctx.nodeId).varsWritten, obj.name);
      }
      Object.values(obj).forEach((v) => walk(v, ctx));
    }
  };

  // -- page nodes --------------------------------------------------------
  const visitNode = (n: PageNode) => {
    const label = nodeLabel(n);
    if (n.type === "custom_component") refs.hasOpaque = true;
    const p = n.props ?? {};

    // Deep-scan every prop (catches {{ }} reads anywhere + set_variable writes
    // inside action chains), attributing the find to the top-level prop key.
    for (const [key, val] of Object.entries(p)) {
      walk(val, { nodeId: n.id, label, field: key, isNode: true });
    }
    // visible_when is a {{ }} boolean expression.
    if (typeof n.visible_when === "string") {
      walk(n.visible_when, { nodeId: n.id, label, field: "visible_when", isNode: true });
    }
    // Structured references the deep-scan can't infer from a bare string.
    const sctx = (field: string): Ctx => ({ nodeId: n.id, label, field, isNode: true });
    // ADR-0044 U1: a widget reads a query via the `data` binding
    // (`{{ $queries.x.data }}`) — a `{{ }}` string, so the deep-scan above
    // already registers its `$queries.*` read (and the inverse index). No
    // separate handling is needed now that `query_ref` is gone.
    if (typeof p.state_key === "string" && p.state_key) {
      addVar(p.state_key, mk(sctx("state_key"), "write"));
      uniqPush(node(n.id).varsWritten, p.state_key);
    }
    if (typeof p.select_into === "string" && p.select_into) {
      addVar(p.select_into, mk(sctx("select_into"), "write"));
      uniqPush(node(n.id).varsWritten, p.select_into);
    }
    (n.children ?? []).forEach(visitNode);
  };
  if (ast.root) visitNode(ast.root);

  // -- queries as consumers (their {{ }} params + deps read page vars) ---
  for (const q of ast.queries ?? []) {
    if (q.source_kind === "js") refs.hasOpaque = true;
    const label = `query · ${q.name}`;
    // Templated fields that can read $page / $queries.
    const fields: (keyof PageQuery)[] = ["filters", "sort", "url", "query", "body", "value", "code", "result_path", "headers"];
    for (const f of fields) {
      if (q[f] !== undefined) walk(q[f], { nodeId: q.id, label, field: String(f), isNode: false });
    }
    for (const dep of q.deps ?? []) {
      addVar(dep, mk({ nodeId: q.id, label, field: "deps", isNode: false }, "read"));
    }
  }

  return refs;
}

/** Total usage count for a query (all consumers). */
export function queryUsageCount(refs: PageReferences, name: string): number {
  return refs.query[name]?.length ?? 0;
}
/** Total usage count for a variable (reads + writes). */
export function varUsageCount(refs: PageReferences, name: string): number {
  return refs.var[name]?.length ?? 0;
}

/** Distinct consumer labels for a set of usages, in first-seen order. Powers the
 *  delete-guard ("used by: Tabella deals, Testo Titolo") and the usage list. */
export function consumerLabels(usages: Usage[] | undefined): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const u of usages ?? []) {
    if (!seen.has(u.label)) {
      seen.add(u.label);
      out.push(u.label);
    }
  }
  return out;
}
